import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as TourSuppliersService from '~/services/TourSuppliersService';

import { CreateServicePayload, UpdateServiceCostingPayload, UpdateSupplierPayload } from '~/types/tours/supplier';

import { costPriceQueryKeys } from './useTourCostPrices';

export const supplierQueryKeys = {
  all: ['tour-suppliers'] as const,
  lists: () => [...supplierQueryKeys.all, 'list'] as const,
  list: (name: string) => [...supplierQueryKeys.lists(), name] as const,
  details: () => [...supplierQueryKeys.all, 'detail'] as const,
  detail: (supplierId: string | undefined) => [...supplierQueryKeys.details(), supplierId ?? ''] as const,
  services: (supplierId: string | undefined) => [...supplierQueryKeys.all, 'services', supplierId ?? ''] as const,
};

export function useGetSuppliers(name = '') {
  return useQuery({
    queryKey: supplierQueryKeys.list(name),
    queryFn: ({ signal }) => TourSuppliersService.getSuppliers({ name: name || undefined }, { signal }),
  });
}

export function useGetSupplier(supplierId: string | undefined) {
  return useQuery({
    queryKey: supplierQueryKeys.detail(supplierId),
    queryFn: ({ signal }) => TourSuppliersService.getSupplier(supplierId, { signal }),
    enabled: !!supplierId,
  });
}

/** Services carry their supplier's profit share rate, so their cost prices are refreshed too. */
export function useUpdateSupplier(supplierId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateSupplierPayload) => TourSuppliersService.updateSupplier(supplierId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: costPriceQueryKeys.all });
    },
  });
}

export function useGetSupplierServices(supplierId: string | undefined) {
  return useQuery({
    queryKey: supplierQueryKeys.services(supplierId),
    queryFn: ({ signal }) => TourSuppliersService.getSupplierServices(supplierId, { signal }),
    enabled: !!supplierId,
  });
}

/** Also links the tour, so the tour's service list is refreshed too. */
export function useCreateService(supplierId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateServicePayload) => TourSuppliersService.createService(supplierId, payload),
    onSuccess: (_service, payload) => {
      queryClient.invalidateQueries({ queryKey: supplierQueryKeys.services(supplierId) });
      queryClient.invalidateQueries({ queryKey: costPriceQueryKeys.tourServices(payload.tourId) });
    },
  });
}

/** Refreshes the tour's services and the service's cost prices, which a costing change can remove. */
export function useUpdateServiceCosting({
  supplierId,
  serviceId,
  tourId,
}: {
  supplierId: string;
  serviceId: string;
  tourId: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateServiceCostingPayload) =>
      TourSuppliersService.updateServiceCosting(supplierId, serviceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierQueryKeys.services(supplierId) });
      queryClient.invalidateQueries({ queryKey: costPriceQueryKeys.tourServices(tourId) });
      queryClient.invalidateQueries({ queryKey: costPriceQueryKeys.service({ supplierId, serviceId }) });
    },
  });
}
